<script setup>
import {ref, onMounted} from 'vue'
import {useMagicKeys} from '@vueuse/core'
import keys from '@admin_js/ValueObjects/keys'
import {UseDraggable} from '@vueuse/components'
import {MinusIcon, PlusIcon} from '@heroicons/vue/outline'

const props = defineProps({
  device: {
    required: true,
  },
})

onMounted(
  () => {
    recording.value = sessionStorage.getItem('recording') === 'true'
    minimized.value = sessionStorage.getItem('minimized') === 'true'
  }
)

const {current} = useMagicKeys({
  passive: false,
  onEventFired(e) {
    e.preventDefault()
  },
})
const keyboard = ref(null)
const recording = ref(true)
const minimized = ref(false)
const emit = defineEmits(['pressed'])

const toggleRecording = () => {
  recording.value = !recording.value
  sessionStorage.setItem('recording', recording.value)
}

const toggleMinimized = () => {
  minimized.value = !minimized.value
  sessionStorage.setItem('minimized', minimized.value)
}

document.addEventListener(
  'keydown',
  function (e) {
    if (recording.value) {
      const key = keys.filter(
        (item) => item.label.toLowerCase() === e.key.toLowerCase()
      )
      if (key.length >= 1) {
        emit('pressed', {
          modifiers: getModifiers(),
          ...key[0],
        })
      }
    }
  },
  false
)

const getModifiers = () => {
  const modifiers = {
    SHIFT: current.has('ShiftLeft'),
    RIGHT_SHIFT: current.has('ShiftRight'),
    LEFT_CTRL: current.has('ControlLeft'),
    RIGHT_CTRL: current.has('ControlRight'),
    LEFT_ALT: current.has('AltLeft'),
    RIGHT_ALT: current.has('AltRight'),
    LEFT_GUI: current.has('MetaLeft'),
    RIGHT_GUI: current.has('MetaRight'),
    //SuperLeft: current.has('SuperLeft'),
    //SuperRight: current.has('SuperRight'),
    //HyperLeft: current.has('HyperLeft'),
    //HyperRight: current.has('HyperRight'),
  }

  const result = []

  for (const [key, value] of Object.entries(modifiers)) {
    if (modifiers[key]) {
      result.push(key)
    }
  }

  return result
}
</script>

<template>
  <UseDraggable
    class="fixed z-50 flex flex-col rounded bg-slate-600 bg-opacity-75 py-2 px-2"
    :storage-key="props.device.external_identifier"
    storage-type="session"
  >
    <div class="flex h-full w-full cursor-move justify-end items-center text-sm text-white">
      <div
        :class="[
          recording ? 'bg-green-400' : 'bg-red-400',
          'my-2 flex h-6 cursor-pointer rounded px-2 py-0.5 text-xs leading-5 text-white',
        ]"
        @click="toggleRecording"
      >
        <span>
          {{ recording ? 'Recording' : 'Not Recording' }}
        </span>
      </div>
      <div @click="toggleMinimized" class="cursor-pointer px-2 py-0.5">
        <MinusIcon v-if="!minimized" class="w-6 h-6"/>
        <PlusIcon v-else class="w-6 h-6"/>
      </div>
    </div>
    <div class="overflow-hidden rounded p-1" v-if="!minimized && keys && keys.length > 0">
      <div class="flex space-x-[2px]" v-for="row in [0, 1, 2, 3, 4, 5]">
        <div
          v-for="filtered in keys.filter((item) => item.row == row)"
          :key="filtered['label']"
          class="min-w-10 h-10"
          :class="[filtered['flexGrow'] ? 'flex-grow' : '', '']"
        >
          <button
            @click="emit('pressed', filtered)"
            class="align-center relative top-0 flex h-10 justify-center overflow-hidden rounded bg-gradient-to-b from-slate-50 to-slate-400 px-1 pt-[2px] shadow transition-all duration-75 active:top-1"
            :class="'w-' + [filtered['width']]"
          >
            <div
              class="absolute -top-[2px] left-0 flex h-10 w-full items-center justify-between blur-sm"
            >
              <div
                class="relative -left-5 h-8 w-8 flex-shrink-0 rotate-45 bg-slate-200"
              ></div>
              <div
                class="relative -right-5 h-8 w-8 flex-shrink-0 rotate-45 bg-slate-200"
              ></div>
            </div>
            <div
              class="relative flex h-7 flex-grow rounded border border-slate-100 bg-gradient-to-b from-slate-200 to-slate-50 pt-1 pl-1"
            >
              <span class="leading-none">{{ filtered['label'] }}</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </UseDraggable>
</template>
